import { MutableRefObject, useCallback, useRef } from 'react';

interface UseStagePanTweenParams {
  positionRef: MutableRefObject<{ x: number; y: number }>;
  setPosition: (position: { x: number; y: number }) => void;
}

interface UseStagePanTweenApi {
  animateStagePositionTo: (toX: number, toY: number) => void;
  clearPanTween: () => void;
}

const PAN_DURATION_MS = 280;

const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

export const useStagePanTween = (params: UseStagePanTweenParams): UseStagePanTweenApi => {
  const { positionRef, setPosition } = params;
  const panTweenRafRef = useRef<number>(0);

  const clearPanTween = useCallback((): void => {
    if (panTweenRafRef.current) {
      cancelAnimationFrame(panTweenRafRef.current);
      panTweenRafRef.current = 0;
    }
  }, []);

  const animateStagePositionTo = useCallback(
    (toX: number, toY: number): void => {
      clearPanTween();

      const from = { ...positionRef.current };
      const startTime = performance.now();

      let frameIndex = 0;

      const step = (now: number): void => {
        const rawT = Math.min(1, (now - startTime) / PAN_DURATION_MS);
        const t = easeOutCubic(rawT);
        const x = from.x + (toX - from.x) * t;
        const y = from.y + (toY - from.y) * t;

        positionRef.current = { x, y };
        frameIndex += 1;

        const isFinal = rawT >= 1;

        if (isFinal || frameIndex === 1 || frameIndex % 2 === 0) {
          setPosition({ x, y });
        }

        if (rawT < 1) {
          panTweenRafRef.current = requestAnimationFrame(step);
        } else {
          panTweenRafRef.current = 0;
        }
      };

      panTweenRafRef.current = requestAnimationFrame(step);
    },
    [clearPanTween, positionRef, setPosition],
  );

  return { animateStagePositionTo, clearPanTween };
};
